import type { Building } from "building-types/building/buildingTypes";
import deepequal from "common/utils/deepequal";
import noop from "common/utils/noop";
import shallowCompare from "common/utils/shallowCompare";

export default function makeGenerationStageComparator<T extends (v: Building) => any>(
  comparisonType: { [K in keyof ReturnType<T>]?: "shallow" | "deep" | "ignore" } = {},
  notifyKeyHasChanged: (k: keyof ReturnType<T>) => void = noop,
): (t: ReturnType<T>, t2: ReturnType<T>) => boolean {
  return makeObjectComparator<Building, T>(comparisonType, notifyKeyHasChanged);
}

export const makeBuildingQueryComparator = makeGenerationStageComparator;

export function makeObjectComparator<V, T extends (v: V) => any>(
  comparisonType: { [K in keyof ReturnType<T>]?: "shallow" | "deep" | "ignore" } = {},
  notifyKeyHasChanged: (k: keyof ReturnType<T>) => void = noop,
): (t: ReturnType<T>, t2: ReturnType<T>) => boolean {
  return (oldT: ReturnType<T>, newT: ReturnType<T>) => {
    if (!oldT || !newT) {
      return false;
    }

    const oldPropNames = Object.getOwnPropertyNames(oldT) as Array<keyof typeof oldT>;
    const newPropNames = Object.getOwnPropertyNames(newT) as Array<keyof typeof newT>;

    if (oldPropNames.length !== newPropNames.length) {
      return false;
    }

    for (const k of oldPropNames) {
      const oldV = oldT[k];
      const newV = newT[k];

      if (comparisonType[k] === "ignore") {
        // if the key is set to ignore, we skip it
        continue;
      } else if (comparisonType[k] === "shallow") {
        // if the two values are objects
        if (oldV && typeof oldV === "object" && newV && typeof newV === "object") {
          if (!shallowCompare(oldV, newV)) {
            notifyKeyHasChanged(k);
            return false;
          }
        } else {
          // otherwise we use reference comparison
          if (oldV !== newV) {
            notifyKeyHasChanged(k);
            return false;
          }
        }
      } else if (comparisonType[k] === "deep") {
        if (!deepequal(oldV, newV)) {
          return false;
        }
      } else {
        // default is reference comparison
        if (oldV !== newV) {
          notifyKeyHasChanged(k);
          return false;
        }
      }
    }

    return true;
  };
}
